import React, {
  useEffect,
  useMemo,
  useState,
} from 'react'

import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
  Spin,
} from 'antd'

import {
  CheckCircleFilled,
} from '@ant-design/icons'

import {
  useParams,
} from 'react-router-dom'

import fetch from 'auth/FetchInterceptor'

import {
  env,
} from 'configs/EnvironmentConfig'

import './game-registration.css'


const {
  TextArea,
} = Input


// ======================================================
// API
// ======================================================

const API_BASE_URL =
  env.API_ENDPOINT_URL


// ======================================================
// LOGO FIXA DO JOB
// ======================================================

const ADEMICON_LOGO_URL =
  '/img/river-race/logo-ademicon.png'


// ======================================================
// HELPERS
// ======================================================

const onlyNumbers = (
  value = '',
) => {
  return String(
    value,
  ).replace(
    /\D/g,
    '',
  )
}


// ======================================================
// TELEFONE
// ======================================================

const formatPhone = (
  value = '',
) => {
  const numbers =
    onlyNumbers(
      value,
    ).slice(
      0,
      11,
    )


  if (
    numbers.length <=
    2
  ) {
    return numbers
  }


  if (
    numbers.length <=
    6
  ) {
    return (
      `(${numbers.slice(0, 2)}) ` +
      numbers.slice(2)
    )
  }


  if (
    numbers.length <=
    10
  ) {
    return (
      `(${numbers.slice(0, 2)}) ` +
      `${numbers.slice(2, 6)}-` +
      numbers.slice(6)
    )
  }


  return (
    `(${numbers.slice(0, 2)}) ` +
    `${numbers.slice(2, 7)}-` +
    numbers.slice(7)
  )
}


// ======================================================
// CPF
// ======================================================

const formatCpf = (
  value = '',
) => {
  const numbers =
    onlyNumbers(
      value,
    ).slice(
      0,
      11,
    )


  if (
    numbers.length <=
    3
  ) {
    return numbers
  }


  if (
    numbers.length <=
    6
  ) {
    return (
      `${numbers.slice(0, 3)}.` +
      numbers.slice(3)
    )
  }


  if (
    numbers.length <=
    9
  ) {
    return (
      `${numbers.slice(0, 3)}.` +
      `${numbers.slice(3, 6)}.` +
      numbers.slice(6)
    )
  }


  return (
    `${numbers.slice(0, 3)}.` +
    `${numbers.slice(3, 6)}.` +
    `${numbers.slice(6, 9)}-` +
    numbers.slice(9)
  )
}


// ======================================================
// NORMALIZAR OPTIONS
// ======================================================

const normalizeOptions = (
  options,
) => {
  if (
    !Array.isArray(
      options,
    )
  ) {
    return []
  }


  return options.map(
    (
      option,
      index,
    ) => {
      if (
        typeof option ===
        'string'
      ) {
        return {
          label:
            option,

          value:
            option,
        }
      }


      if (
        option &&
        typeof option ===
        'object'
      ) {
        return {
          label:
            option.label ??
            option.name ??
            option.value ??
            `Opção ${index + 1}`,

          value:
            option.value ??
            option.name ??
            option.label ??
            String(
              index,
            ),
        }
      }


      return {
        label:
          String(
            option,
          ),

        value:
          String(
            option,
          ),
      }
    },
  )
}


// ======================================================
// VALOR VAZIO
// ======================================================

const isEmptyValue = (
  value,
) => {
  if (
    value === undefined ||
    value === null ||
    value === ''
  ) {
    return true
  }


  if (
    Array.isArray(
      value,
    ) &&
    value.length ===
    0
  ) {
    return true
  }


  return false
}


// ======================================================
// PAGE
// ======================================================

const GameRegistrationPage =
  () => {
    const {
      publicToken,
    } =
      useParams()


    const [
      form,
    ] =
      Form.useForm()


    // ==================================================
    // STATE
    // ==================================================

    const [
      loading,
      setLoading,
    ] =
      useState(
        true,
      )


    const [
      submitting,
      setSubmitting,
    ] =
      useState(
        false,
      )


    const [
      session,
      setSession,
    ] =
      useState(
        null,
      )


    const [
      participant,
      setParticipant,
    ] =
      useState(
        null,
      )


    const [
      error,
      setError,
    ] =
      useState(
        '',
      )


    const [
      success,
      setSuccess,
    ] =
      useState(
        false,
      )


    // ======================================================
    // RETORNO AUTOMÁTICO AO LEITOR DE QR
    //
    // Só acontece quando o cadastro foi aberto pelo
    // /qr-reader através de ?reader=1.
    //
    // Acesso normal ao /join/:publicToken continua igual.
    // ======================================================

    useEffect(
      () => {
        if (!success) {
          return undefined
        }


        const searchParams =
          new URLSearchParams(
            window.location.search,
          )


        const cameFromReader =
          searchParams.get(
            'reader',
          ) === '1'


        if (!cameFromReader) {
          return undefined
        }


        const timer =
          window.setTimeout(
            () => {
              window.location.replace(
                '/qr-reader',
              )
            },
            3000,
          )


        return () => {
          window.clearTimeout(
            timer,
          )
        }
      },
      [
        success,
      ],
    )



    // ==================================================
    // GAME / EVENT / FORM
    // ==================================================

    const game =
      session?.game ||
      null


    const event =
      game?.event ||
      null


    const leadForm =
      session?.form ||
      null


    const fields =
      useMemo(
        () => {
          const source =
            leadForm?.fields


          if (
            !Array.isArray(
              source,
            )
          ) {
            return []
          }


          return [
            ...source,
          ].sort(
            (
              a,
              b,
            ) =>
              Number(
                a.order ||
                0,
              ) -
              Number(
                b.order ||
                0,
              ),
          )
        },
        [
          leadForm,
        ],
      )


    const gameName =
      useMemo(
        () => {
          return (
            game?.name ||
            'River Race Ademicon'
          )
        },
        [
          game,
        ],
      )


    // ==================================================
    // LOAD SESSION
    // ==================================================

    useEffect(
      () => {
        const loadSession =
          async () => {
            if (
              !publicToken
            ) {
              setError(
                'Sessão inválida.',
              )


              setLoading(
                false,
              )


              return
            }


            try {
              setLoading(
                true,
              )


              setError(
                '',
              )


              const response =
                await fetch({
                  url:
                    `${API_BASE_URL}/game-sessions/public/${publicToken}`,

                  method:
                    'get',
                })


              setSession(
                response,
              )


              if (
                response?.status ===
                'EXPIRED'
              ) {
                setError(
                  'Esta participação expirou. Solicite um novo QR Code.',
                )


                return
              }


              if (
                response?.status ===
                'CONFIRMED' ||
                response?.status ===
                'PLAYING' ||
                response?.status ===
                'FINISHED'
              ) {
                setParticipant(
                  response?.participant ||
                  null,
                )


                setSuccess(
                  true,
                )
              }


              if (
                response?.status ===
                'WAITING' &&
                !response?.form
              ) {
                setError(
                  'Nenhum formulário de captação está configurado para este jogo.',
                )
              }
            }
            catch (
            err
            ) {
              console.error(
                '[GameRegistration] Erro carregando sessão:',
                err,
              )


              const message =
                err?.response?.data?.message ||
                err?.message ||
                'Não foi possível carregar esta participação.'


              setError(
                Array.isArray(
                  message,
                )
                  ? message.join(
                    ' ',
                  )
                  : message,
              )
            }
            finally {
              setLoading(
                false,
              )
            }
          }


        loadSession()
      },
      [
        publicToken,
      ],
    )


    // ==================================================
    // NORMALIZAR VALUES
    // ==================================================

    const normalizeSubmitValues =
      (
        rawValues,
      ) => {
        const result = {}


        fields.forEach(
          (
            field,
          ) => {
            const key =
              field.fieldKey


            let value =
              rawValues[
              key
              ]


            if (
              field.fieldType ===
              'PHONE'
            ) {
              value =
                value
                  ? onlyNumbers(
                    value,
                  )
                  : ''
            }


            if (
              field.fieldType ===
              'CPF'
            ) {
              value =
                value
                  ? onlyNumbers(
                    value,
                  )
                  : ''
            }


            if (
              field.fieldType ===
              'DATE' &&
              value
            ) {
              if (
                typeof value.format ===
                'function'
              ) {
                value =
                  value.format(
                    'YYYY-MM-DD',
                  )
              }
            }


            if (
              field.fieldType ===
              'NUMBER' &&
              value !==
              undefined &&
              value !==
              null &&
              value !==
              ''
            ) {
              value =
                Number(
                  value,
                )
            }


            result[
              key
            ] =
              value
          },
        )


        return result
      }


    // ==================================================
    // SUBMIT
    // ==================================================

    const handleSubmit =
      async (
        values,
      ) => {
        if (
          submitting
        ) {
          return
        }


        try {
          setSubmitting(
            true,
          )


          setError(
            '',
          )


          const dynamicValues =
            normalizeSubmitValues(
              values,
            )


          const payload = {
            consentLgpd:
              values.consentLgpd ===
              true,

            values:
              dynamicValues,
          }


          const response =
            await fetch({
              url:
                `${API_BASE_URL}/game-sessions/public/${publicToken}/register`,

              method:
                'post',

              data:
                payload,
            })


          setParticipant(
            response?.participant ||
            {
              name:
                dynamicValues.nome ||
                'JOGADOR',

              email:
                dynamicValues.email,

              phone:
                dynamicValues.telefone,
            },
          )


          setSuccess(
            true,
          )
        }
        catch (
        err
        ) {
          console.error(
            '[GameRegistration] Erro confirmando participação:',
            err,
          )


          const message =
            err?.response?.data?.message ||
            err?.message ||
            'Não foi possível confirmar a participação.'


          setError(
            Array.isArray(
              message,
            )
              ? message.join(
                ' ',
              )
              : message,
          )
        }
        finally {
          setSubmitting(
            false,
          )
        }
      }


    // ==================================================
    // RENDER FIELD
    // ==================================================

    const renderField =
      (
        field,
        index,
      ) => {
        const options =
          normalizeOptions(
            field.options,
          )


        const rules = []


        if (
          field.isRequired
        ) {
          rules.push({
            validator:
              (
                _,
                value,
              ) => {
                if (
                  !isEmptyValue(
                    value,
                  )
                ) {
                  return Promise.resolve()
                }


                return Promise.reject(
                  new Error(
                    `Informe: ${field.label}`,
                  ),
                )
              },
          })
        }


        if (
          field.fieldType ===
          'EMAIL'
        ) {
          rules.push({
            type:
              'email',

            message:
              'Informe um e-mail válido.',
          })
        }


        if (
          field.fieldType ===
          'PHONE'
        ) {
          rules.push({
            validator:
              (
                _,
                value,
              ) => {
                if (
                  !value &&
                  !field.isRequired
                ) {
                  return Promise.resolve()
                }


                const phone =
                  onlyNumbers(
                    value,
                  )


                if (
                  phone.length >=
                  10
                ) {
                  return Promise.resolve()
                }


                return Promise.reject(
                  new Error(
                    'Informe um telefone válido.',
                  ),
                )
              },
          })
        }


        if (
          field.fieldType ===
          'CPF'
        ) {
          rules.push({
            validator:
              (
                _,
                value,
              ) => {
                if (
                  !value &&
                  !field.isRequired
                ) {
                  return Promise.resolve()
                }


                if (
                  onlyNumbers(
                    value,
                  ).length ===
                  11
                ) {
                  return Promise.resolve()
                }


                return Promise.reject(
                  new Error(
                    'Informe um CPF válido.',
                  ),
                )
              },
          })
        }


        let component =
          null


        switch (
        field.fieldType
        ) {
          case 'EMAIL':
            component = (
              <Input
                size="large"
                type="email"
                placeholder={
                  field.placeholder ||
                  'Digite seu e-mail'
                }
                autoComplete="email"
                inputMode="email"
                autoFocus={
                  index ===
                  0
                }
              />
            )

            break


          case 'PHONE':
            component = (
              <Input
                size="large"
                placeholder={
                  field.placeholder ||
                  '(11) 99999-9999'
                }
                autoComplete="tel"
                inputMode="tel"
                maxLength={
                  15
                }
                onChange={
                  (
                    event,
                  ) => {
                    form.setFieldValue(
                      field.fieldKey,
                      formatPhone(
                        event.target.value,
                      ),
                    )
                  }
                }
                autoFocus={
                  index ===
                  0
                }
              />
            )

            break


          case 'CPF':
            component = (
              <Input
                size="large"
                placeholder={
                  field.placeholder ||
                  '000.000.000-00'
                }
                inputMode="numeric"
                maxLength={
                  14
                }
                onChange={
                  (
                    event,
                  ) => {
                    form.setFieldValue(
                      field.fieldKey,
                      formatCpf(
                        event.target.value,
                      ),
                    )
                  }
                }
                autoFocus={
                  index ===
                  0
                }
              />
            )

            break


          case 'NUMBER':
            component = (
              <InputNumber
                size="large"
                style={{
                  width:
                    '100%',
                }}
                placeholder={
                  field.placeholder ||
                  ''
                }
                autoFocus={
                  index ===
                  0
                }
              />
            )

            break


          case 'DATE':
            component = (
              <DatePicker
                size="large"
                style={{
                  width:
                    '100%',
                }}
                format="DD/MM/YYYY"
                placeholder={
                  field.placeholder ||
                  'Selecione a data'
                }
              />
            )

            break


          case 'SELECT':
            component = (
              <Select
                size="large"
                placeholder={
                  field.placeholder ||
                  'Selecione'
                }
                options={
                  options
                }
                showSearch
                optionFilterProp="label"
              />
            )

            break


          case 'RADIO':
            component = (
              <Radio.Group>
                {
                  options.map(
                    (
                      option,
                    ) => (
                      <Radio
                        key={
                          option.value
                        }
                        value={
                          option.value
                        }
                      >
                        {option.label}
                      </Radio>
                    ),
                  )
                }
              </Radio.Group>
            )

            break


          case 'CHECKBOX':
            component = (
              <Checkbox.Group
                options={
                  options
                }
              />
            )

            break


          case 'TEXTAREA':
            component = (
              <TextArea
                rows={
                  4
                }
                placeholder={
                  field.placeholder ||
                  ''
                }
                autoFocus={
                  index ===
                  0
                }
              />
            )

            break


          case 'TEXT':
          default:
            component = (
              <Input
                size="large"
                placeholder={
                  field.placeholder ||
                  ''
                }
                autoFocus={
                  index ===
                  0
                }
              />
            )

            break
        }


        return (
          <Form.Item
            key={
              field.id ||
              field.fieldKey
            }
            label={
              field.label
            }
            name={
              field.fieldKey
            }
            rules={
              rules
            }
          >
            {component}
          </Form.Item>
        )
      }


    // ==================================================
    // LOADING
    // ==================================================

    if (
      loading
    ) {
      return (
        <div className="game-register-loading">

          <Spin
            size="large"
          />


          <div className="game-register-loading-text">
            Preparando participação...
          </div>

        </div>
      )
    }


    // ==================================================
    // ERROR
    // ==================================================

    if (
      error &&
      !session
    ) {
      return (
        <div className="game-register-page">

          <div className="game-register-card game-register-error-card">

            <div className="game-register-error-title">
              Não foi possível abrir a participação
            </div>


            <Alert
              type="error"
              showIcon
              message={
                error
              }
            />

          </div>

        </div>
      )
    }


    // ==================================================
    // EXPIRED
    // ==================================================

    if (
      session?.status ===
      'EXPIRED'
    ) {
      return (
        <div className="game-register-page">

          <div className="game-register-card game-register-error-card">

            <div className="game-register-error-title">
              Participação expirada
            </div>


            <Alert
              type="warning"
              showIcon
              message={
                error ||
                'Solicite um novo QR Code no totem.'
              }
            />

          </div>

        </div>
      )
    }


    // ==================================================
    // SUCCESS
    // ==================================================

    if (
      success
    ) {
      return (
        <div className="game-register-page">

          <div className="game-register-card game-register-success-card">

            <div className="game-register-logo-wrapper">

              <img
                src={
                  ADEMICON_LOGO_URL
                }
                alt="Ademicon"
                className="game-register-logo"
              />

            </div>


            <div className="game-register-success-icon">
              <CheckCircleFilled />
            </div>


            <div className="game-register-success-title">
              PARTICIPAÇÃO CONFIRMADA!
            </div>


            <div className="game-register-success-player">
              {(
                participant?.name ||
                'JOGADOR'
              ).toUpperCase()}
            </div>


            <div className="game-register-success-message">
              O jogo foi liberado!
            </div>


            <div className="game-register-success-message-secondary">
              Volte sua atenção para a tela do River Race.
            </div>


            <div className="game-register-success-status">
              AGUARDE A TELA DE REGRAS E PRESSIONE START NO CONTROLE
            </div>


            <div className="game-register-footer">
              Juntos por novos horizontes
            </div>

          </div>

        </div>
      )
    }


    // ==================================================
    // SEM FORM
    // ==================================================

    if (
      !leadForm
    ) {
      return (
        <div className="game-register-page">

          <div className="game-register-card game-register-error-card">

            <div className="game-register-error-title">
              Cadastro indisponível
            </div>


            <Alert
              type="warning"
              showIcon
              message={
                error ||
                'Nenhum formulário está configurado para este jogo.'
              }
            />

          </div>

        </div>
      )
    }


    // ==================================================
    // FORM
    // ==================================================

    return (
      <div className="game-register-page">

        <div className="game-register-card">

          {/* ============================================= */}
          {/* HEADER */}
          {/* ============================================= */}

          <div className="game-register-header">

            <div className="game-register-logo-wrapper">

              <img
                src={
                  ADEMICON_LOGO_URL
                }
                alt="Ademicon"
                className="game-register-logo"
              />

            </div>


            <div className="game-register-title">
              {gameName}
            </div>


            <div className="game-register-subtitle">
              {
                leadForm.description ||
                'Cadastre o participante para liberar o jogo'
              }
            </div>


            {
              event?.name
                ? (
                  <div className="game-register-event">
                    {event.name}
                  </div>
                )
                : null
            }

          </div>


          {/* ============================================= */}
          {/* ERROR */}
          {/* ============================================= */}

          {
            error
              ? (
                <Alert
                  className="game-register-alert"
                  type="error"
                  showIcon
                  message={
                    error
                  }
                />
              )
              : null
          }


          {/* ============================================= */}
          {/* FORM */}
          {/* ============================================= */}

          <Form
            form={
              form
            }
            layout="vertical"
            requiredMark={
              false
            }
            initialValues={{
              consentLgpd:
                false,
            }}
            onFinish={
              handleSubmit
            }
          >

            {
              fields.map(
                (
                  field,
                  index,
                ) =>
                  renderField(
                    field,
                    index,
                  ),
              )
            }


            {
              leadForm.requireConsent
                ? (
                  <Form.Item
                    name="consentLgpd"
                    valuePropName="checked"
                    rules={[
                      {
                        validator:
                          (
                            _,
                            value,
                          ) => {
                            if (
                              value
                            ) {
                              return Promise.resolve()
                            }


                            return Promise.reject(
                              new Error(
                                'É necessário aceitar para continuar.',
                              ),
                            )
                          },
                      },
                    ]}
                  >
                    <Checkbox>
                      {
                        leadForm.consentText ||
                        'Autorizo o uso dos dados informados para esta participação e ações relacionadas ao evento.'
                      }
                    </Checkbox>
                  </Form.Item>
                )
                : null
            }


            <Button
              type="primary"
              htmlType="submit"
              size="large"
              block
              loading={
                submitting
              }
              className="game-register-button"
            >
              CONFIRMAR PARTICIPAÇÃO
            </Button>

          </Form>


          <div className="game-register-footer">
            Juntos por novos horizontes
          </div>

        </div>

      </div>
    )
  }


export default GameRegistrationPage